import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from './icons/Icon';
import './BackgroundAgentsManager.css';
import config from '../config';
import { getBeHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

const BackgroundAgentsManager = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [modal, setModal] = useState({ show: false, type: 'info', title: '', message: '', onConfirm: null });

  // Show custom modal
  const showModal = (type, title, message, onConfirm = null) => {
    setModal({ show: true, type, title, message, onConfirm });
  };

  const closeModal = () => {
    setModal({ show: false, type: 'info', title: '', message: '', onConfirm: null });
  };

  const handleModalConfirm = () => {
    if (modal.onConfirm) {
      modal.onConfirm();
    }
    closeModal();
  };

  // Helper to get the backend URL for background agents
  const getBackendUrl = () => {
    // Use agentUrl if available (for local dev: localhost:8000)
    // Otherwise use /api prefix (for k8s deployments)
    return config.agentUrl || '';
  };

  // Fetch agents from backend
  const fetchAgents = async () => {
    try {
      setLoading(true);
      const url = `${getBackendUrl()}/api/background-agents/agents`;
      const response = await getAuthorizedFetch()(url, {
        headers: getBeHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch agents');
      const data = await response.json();
      setAgents(data.agents || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch executions for a specific agent
  const fetchExecutions = async (agentId) => {
    try {
      const url = `${getBackendUrl()}/api/background-agents/agents/${agentId}/executions`;
      const response = await getAuthorizedFetch()(url, {
        headers: getBeHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch executions');
      const data = await response.json();
      setExecutions(data.executions || []);
    } catch (err) {
      console.error('Error fetching executions:', err);
    }
  };

  // Execute an agent
  const executeAgent = async (agentId) => {
    try {
      const url = `${getBackendUrl()}/api/background-agents/agents/${agentId}/execute`;
      const response = await getAuthorizedFetch()(url, {
        method: 'POST',
        headers: getBeHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ parameters: {} })
      });
      if (!response.ok) throw new Error('Failed to execute agent');
      const execution = await response.json();
      showModal('success', 'Agent Execution Started', `Execution ID: ${execution.id}`);
      fetchExecutions(agentId);
    } catch (err) {
      showModal('error', 'Execution Failed', err.message);
    }
  };

  // Delete an agent
  const deleteAgent = async (agentId) => {
    showModal('confirm', 'Delete Agent', 'Are you sure you want to delete this agent? This action cannot be undone.', async () => {
      try {
        const url = `${getBackendUrl()}/api/background-agents/agents/${agentId}`;
        const response = await getAuthorizedFetch()(url, {
          method: 'DELETE',
          headers: getBeHeaders()
        });
        if (!response.ok) throw new Error('Failed to delete agent');
        showModal('success', 'Agent Deleted', 'Agent deleted successfully');
        fetchAgents();
        if (selectedAgent?.id === agentId) {
          setSelectedAgent(null);
        }
      } catch (err) {
        showModal('error', 'Delete Failed', err.message);
      }
    });
  };

  // Pause/Resume agent
  const toggleAgentStatus = async (agent) => {
    const newStatus = agent.status === 'active' ? 'paused' : 'active';
    try {
      const url = `${getBackendUrl()}/api/background-agents/agents/${agent.id}`;
      const response = await getAuthorizedFetch()(url, {
        method: 'PATCH',
        headers: getBeHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ status: newStatus })
      });
      if (!response.ok) throw new Error('Failed to update agent status');
      fetchAgents();
    } catch (err) {
      showModal('error', 'Status Update Failed', err.message);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    if (selectedAgent) {
      fetchExecutions(selectedAgent.id);
    }
  }, [selectedAgent]);

  const filteredAgents = statusFilter === 'all' 
    ? agents 
    : agents.filter(a => a.status === statusFilter);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return '🟢';
      case 'paused': return '🟡';
      case 'archived': return '⚪';
      default: return '❓';
    }
  };

  const getExecutionStatusIcon = (status) => {
    switch (status) {
      case 'completed': return '✅';
      case 'running': return '🔄';
      case 'failed': return '❌';
      case 'pending': return '⏳';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <div className="agents-loading">
        <div className="hexagon-loader-container">
          <div className="hexagon-loader">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <polygon 
                points="50 1 95 25 95 75 50 99 5 75 5 25" 
                className="hexagon-shape"
              />
            </svg>
          </div>
          <div className="hexagon-loader hexagon-loader-2">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <polygon 
                points="50 1 95 25 95 75 50 99 5 75 5 25" 
                className="hexagon-shape"
              />
            </svg>
          </div>
          <div className="hexagon-loader hexagon-loader-3">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <polygon 
                points="50 1 95 25 95 75 50 99 5 75 5 25" 
                className="hexagon-shape"
              />
            </svg>
          </div>
        </div>
        <p>Loading background agents...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agents-error">
        <Icon name="warning" size={32} />
        <p>Error: {error}</p>
        <button onClick={fetchAgents} className="retry-btn">
          <Icon name="refresh" size={14} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="background-agents-manager">
      {/* Header with Actions */}
      <div className="agents-header">
        <div className="agents-stats">
          <div className="stat-item">
            <span className="stat-label">Total Agents</span>
            <span className="stat-value">{agents.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Active</span>
            <span className="stat-value">{agents.filter(a => a.status === 'active').length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Paused</span>
            <span className="stat-value">{agents.filter(a => a.status === 'paused').length}</span>
          </div>
        </div>
        <div className="agents-actions">
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Agents</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="archived">Archived</option>
          </select>
          <button onClick={fetchAgents} className="agents-header-refresh-btn">
            <Icon name="refresh" size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* Agents Grid */}
      {filteredAgents.length === 0 ? (
        <div className="agents-empty">
          <Icon name="robot" size={48} />
          <h3>No background agents found</h3>
          <p>Background agents run automated tasks in the background.</p>
          <p className="hint">Create agents using the chat interface with the create_background_agent tool.</p>
        </div>
      ) : (
        <div className="agents-grid">
          {filteredAgents.map((agent) => (
            <div 
              key={agent.id} 
              className={`agent-card ${selectedAgent?.id === agent.id ? 'selected' : ''}`}
              onClick={() => setSelectedAgent(agent)}
            >
              <div className="agent-card-header">
                <div className="agent-info">
                  <h3 className="agent-name">
                    {getStatusIcon(agent.status)} {agent.name}
                  </h3>
                  <span className="agent-model">{agent.model || 'claude-3-5-sonnet'}</span>
                </div>
                <div className="agent-badge">
                  {agent.schedule?.type || 'manual'}
                </div>
              </div>

              {agent.description && (
                <p className="agent-description">{agent.description}</p>
              )}

              <div className="agent-meta">
                <div className="meta-item">
                  <Icon name="calendar" size={12} />
                  <span>{new Date(agent.created_at).toLocaleDateString()}</span>
                </div>
                {agent.last_execution_at && (
                  <div className="meta-item">
                    <Icon name="clock" size={12} />
                    <span>Last run: {new Date(agent.last_execution_at).toLocaleString()}</span>
                  </div>
                )}
                {agent.workflow?.steps && (
                  <div className="meta-item">
                    <Icon name="list" size={12} />
                    <span>{agent.workflow.steps.length} steps</span>
                  </div>
                )}
              </div>

              <div className="agent-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    executeAgent(agent.id);
                  }}
                  className="action-btn execute"
                  disabled={agent.status !== 'active'}
                  title="Execute agent now"
                >
                  <Icon name="play" size={14} />
                  Execute
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleAgentStatus(agent);
                  }}
                  className="action-btn pause"
                  title={agent.status === 'active' ? 'Pause agent' : 'Resume agent'}
                >
                  <Icon name={agent.status === 'active' ? 'pause' : 'play'} size={14} />
                  {agent.status === 'active' ? 'Pause' : 'Resume'}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteAgent(agent.id);
                  }}
                  className="action-btn delete"
                  title="Delete agent"
                >
                  <Icon name="trash" size={14} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selected Agent Details Panel */}
      {selectedAgent && (
        <div className="agent-details-panel">
          <div className="panel-header">
            <h3>
              <Icon name="robot" size={20} />
              {selectedAgent.name} - Executions
            </h3>
            <button 
              onClick={() => setSelectedAgent(null)}
              className="close-btn"
            >
              <Icon name="x" size={16} />
            </button>
          </div>

          <div className="executions-list">
            {executions.length === 0 ? (
              <div className="executions-empty">
                <p>No executions yet</p>
              </div>
            ) : (
              executions.map((execution) => (
                <div key={execution.id} className="execution-item">
                  <div className="execution-header">
                    <span className="execution-status">
                      {getExecutionStatusIcon(execution.status)} {execution.status}
                    </span>
                    <span className="execution-time">
                      {new Date(execution.started_at).toLocaleString()}
                    </span>
                  </div>
                  {execution.progress && (
                    <div className="execution-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${execution.progress.percentage || 0}%` }}
                        />
                      </div>
                      <span className="progress-text">
                        {execution.progress.current_step_name || 'Processing...'}
                      </span>
                    </div>
                  )}
                  {execution.error_message && (
                    <div className="execution-error">
                      <Icon name="warning" size={12} />
                      {execution.error_message}
                    </div>
                  )}
                  {execution.completed_at && (
                    <div className="execution-duration">
                      Duration: {Math.round((new Date(execution.completed_at) - new Date(execution.started_at)) / 1000)}s
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Custom Modal */}
      {modal.show && createPortal(
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content background-agents-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`modal-header modal-${modal.type}`}>
              <div className="modal-icon">
                {modal.type === 'success' && <Icon name="check" size={24} />}
                {modal.type === 'error' && <Icon name="warning" size={24} />}
                {modal.type === 'confirm' && <Icon name="warning" size={24} />}
                {modal.type === 'info' && <Icon name="info" size={24} />}
              </div>
              <h3>{modal.title}</h3>
              <button onClick={closeModal} className="modal-close-btn">
                <Icon name="x" size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>{modal.message}</p>
            </div>
            <div className="modal-footer">
              {modal.type === 'confirm' ? (
                <>
                  <button onClick={closeModal} className="modal-btn modal-btn-cancel">
                    Cancel
                  </button>
                  <button onClick={handleModalConfirm} className="modal-btn modal-btn-confirm">
                    Confirm
                  </button>
                </>
              ) : (
                <button onClick={closeModal} className="modal-btn modal-btn-ok">
                  OK
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default BackgroundAgentsManager;
